import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { formatNumber } from "utils/format";
import { NumField, ListPicker } from "layouts/simulator/fields";

/** "180 công · 500 HP" — bỏ chỉ số bằng 0, dùng cho dòng mô tả trong danh sách chọn. */
export function statText(obj) {
  if (!obj) return "";
  return [
    ["công", obj.atk],
    ["thủ", obj.def],
    ["HP", obj.hp],
    ["MP", obj.mp],
  ]
    .filter(([, v]) => v > 0)
    .map(([label, v]) => `${formatNumber(v)} ${label}`)
    .join(" · ");
}

/**
 * Dòng hiệu ứng của một kỹ năng bị động ở một cấp, đã kẹp theo cấp tối đa của chính kỹ năng đó.
 * Mỗi kỹ năng có cấp tối đa khác nhau, nên xem trước ở cấp 5 cho một kỹ năng chỉ có 3 cấp sẽ
 * hiện nhầm; kẹp lại thì luôn hiện dòng gần nhất với cái người dùng sẽ nhận được.
 */
export function effectLines(skill, lvl) {
  if (!skill || !skill.levels || skill.levels.length === 0) return [];
  const wanted = Math.min(Math.max(1, lvl || 1), skill.maxLevel);
  const row = skill.levels.find((l) => l.lvl === wanted) || skill.levels[skill.levels.length - 1];
  return row ? row.lines : [];
}

/** Một dòng chữ gọn cho danh sách chọn: "Tăng 6% HP tối đa · Tăng 2% tỉ lệ chí mạng". */
export function effectPreview(skill, lvl) {
  const lines = effectLines(skill, lvl);
  if (lines.length === 0) return skill && skill.description ? skill.description : "";
  return lines.map((l) => l.text).join(" · ");
}

/** Các nguồn chỉ số không nằm trên ô trang bị: hình xăm, danh hiệu và kỹ năng bị động. */
function PetExtras({ build, result, meta, tattoos, achievements, onChange }) {
  const pet = result ? result.pet : null;
  const passiveSlots = meta.passiveSlotLevels.filter((lv) => build.lvl >= lv).length;
  const learnable = meta.passiveSkills.filter(
    (p) => !pet || p.nclass === -1 || p.nclass === pet.nclass
  );

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <MDTypography variant="button" fontWeight="bold">
          Hình xăm
        </MDTypography>
        <MDButton
          size="small"
          variant="outlined"
          color="info"
          onClick={() =>
            onChange({ ...build, tattoos: [...build.tattoos, { templateId: null, lvl: 0 }] })
          }
        >
          <Icon>add</Icon>&nbsp;Thêm ô
        </MDButton>
      </MDBox>
      {build.tattoos.length === 0 && (
        <MDTypography variant="caption" color="text">
          Chưa có hình xăm nào
        </MDTypography>
      )}
      {build.tattoos.map((tattoo, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Grid container spacing={1} alignItems="center" mb={1} key={index}>
          <Grid item xs={7}>
            <ListPicker
              label={`Ô ${index + 1}`}
              value={tattoo.templateId}
              options={tattoos}
              iconKey="iconPath"
              fallback="brush"
              note={(t) => `${statText(t)} (gốc, +10 là gấp đôi)`}
              onChange={(templateId) => {
                const next = [...build.tattoos];
                next[index] = { ...next[index], templateId };
                onChange({ ...build, tattoos: next });
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <NumField
              label="Cấp"
              value={tattoo.lvl}
              min={0}
              max={10}
              onChange={(lvl) => {
                const next = [...build.tattoos];
                next[index] = { ...next[index], lvl };
                onChange({ ...build, tattoos: next });
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <MDButton
              size="small"
              variant="text"
              color="error"
              iconOnly
              onClick={() =>
                onChange({ ...build, tattoos: build.tattoos.filter((unused, i) => i !== index) })
              }
            >
              <Icon>delete</Icon>
            </MDButton>
          </Grid>
        </Grid>
      ))}

      <Divider sx={{ my: 2 }} />

      <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
        Danh hiệu
      </MDTypography>
      <ListPicker
        label="Danh hiệu đang dùng"
        value={build.titleId > 0 ? build.titleId : null}
        options={achievements}
        iconKey="iconPath"
        fallback="military_tech"
        note={statText}
        onChange={(titleId) => onChange({ ...build, titleId: titleId || -1 })}
      />

      <Divider sx={{ my: 2 }} />

      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <MDTypography variant="button" fontWeight="bold">
          Kỹ năng bị động
        </MDTypography>
        <MDButton
          size="small"
          variant="outlined"
          color="info"
          disabled={build.passives.length >= passiveSlots}
          onClick={() =>
            onChange({ ...build, passives: [...build.passives, { skillId: null, lvl: 1 }] })
          }
        >
          <Icon>add</Icon>&nbsp;Thêm ô
        </MDButton>
      </MDBox>
      <MDTypography variant="caption" color="text" display="block" mb={1}>
        {`Cấp ${build.lvl} mở ${passiveSlots} / ${
          meta.passiveSlotLevels.length
        } ô (mốc cấp ${meta.passiveSlotLevels.join(
          ", "
        )}). Dòng ghi "vào chỉ số" được cộng vào HP / MP trước trận và hiện ở bảng bên phải; dòng "buff trong trận" chỉ có tác dụng khi đánh nhau nên không làm đổi con số ở đây.`}
      </MDTypography>
      {build.passives.map((passive, index) => {
        const skill = learnable.find((p) => p.skillId === passive.skillId);
        const lines = effectLines(skill, passive.lvl);
        return (
          // eslint-disable-next-line react/no-array-index-key
          <MDBox key={index} mb={1.5}>
            {/* flex-start chứ không phải center: ô "Cấp" có dòng chú thích bên dưới nên cao hơn ô
                bên cạnh — căn giữa thì hai hộp nhập lệch nhau ngay khi chú thích xuất hiện. */}
            <Grid container spacing={1} alignItems="flex-start">
              <Grid item xs={7}>
                {/* Hiệu ứng nằm ngay trong từng dòng của danh sách: phải biết kỹ năng làm gì
                    TRƯỚC khi chọn thì mới chọn được. */}
                <ListPicker
                  label={`Ô ${index + 1}`}
                  value={passive.skillId}
                  options={learnable}
                  idKey="skillId"
                  note={(p) => effectPreview(p, passive.lvl)}
                  onChange={(skillId) => {
                    const next = [...build.passives];
                    next[index] = { ...next[index], skillId };
                    onChange({ ...build, passives: next });
                  }}
                />
              </Grid>
              <Grid item xs={3}>
                <NumField
                  label="Cấp"
                  value={passive.lvl}
                  min={1}
                  max={skill ? skill.maxLevel : 1}
                  onChange={(lvl) => {
                    const next = [...build.passives];
                    next[index] = { ...next[index], lvl };
                    onChange({ ...build, passives: next });
                  }}
                  helper={skill ? `tối đa ${skill.maxLevel}` : null}
                />
              </Grid>
              <Grid item xs={2} mt={0.5}>
                <MDButton
                  size="small"
                  variant="text"
                  color="error"
                  iconOnly
                  onClick={() =>
                    onChange({
                      ...build,
                      passives: build.passives.filter((unused, i) => i !== index),
                    })
                  }
                >
                  <Icon>delete</Icon>
                </MDButton>
              </Grid>
            </Grid>

            {skill && (
              <MDBox pl={0.5} mt={0.25}>
                {lines.length > 0 ? (
                  lines.map((line) => (
                    <MDBox
                      key={`${line.id}-${line.value}`}
                      display="flex"
                      alignItems="center"
                      gap={0.75}
                    >
                      <MDTypography
                        variant="caption"
                        color={line.affectsStats ? "success" : "text"}
                        fontWeight={line.affectsStats ? "medium" : "regular"}
                      >
                        {line.text}
                      </MDTypography>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={line.affectsStats ? "success" : "default"}
                        label={line.affectsStats ? "vào chỉ số" : "buff trong trận"}
                      />
                    </MDBox>
                  ))
                ) : (
                  <MDTypography variant="caption" color="text">
                    {skill.description || "Kỹ năng này không có dòng hiệu ứng nào ở cấp đang chọn"}
                  </MDTypography>
                )}
              </MDBox>
            )}
          </MDBox>
        );
      })}
      {meta.passiveSkills.length === 0 && (
        <Alert severity="info">
          Máy chủ chưa nạp bảng kỹ năng bị động (skill_passive), nên phần này đang rỗng.
        </Alert>
      )}
    </MDBox>
  );
}

PetExtras.defaultProps = { result: null };

PetExtras.propTypes = {
  build: PropTypes.object.isRequired,
  result: PropTypes.object,
  meta: PropTypes.object.isRequired,
  tattoos: PropTypes.arrayOf(PropTypes.object).isRequired,
  achievements: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default PetExtras;
